"use client";

import type { ColumnSizingState } from "@tanstack/react-table";
import type { CSSProperties } from "react";
import { useEffect, useState } from "react";

export type PersistedColumnDefinition<TColumnId extends string> = {
  id: TColumnId;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
};

export function columnWidthStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    flex: `0 0 ${width}px`,
  };
}
export function fluidColumnWidthStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: "none",
    flex: `1 0 ${width}px`,
  };
}

export function clampColumnWidth(
  column: Pick<PersistedColumnDefinition<string>, "minWidth" | "maxWidth">,
  width: number,
): number {
  return Math.min(
    column.maxWidth,
    Math.max(column.minWidth, Math.round(width)),
  );
}

function readStoredColumnSizing(storageKey: string): ColumnSizingState {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as ColumnSizingState)
      : {};
  } catch {
    return {};
  }
}

export function usePersistedColumnSizing(storageKey: string) {
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    readStoredColumnSizing(storageKey),
  );

  useEffect(() => {
    setColumnSizing(readStoredColumnSizing(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey, JSON.stringify(columnSizing));
  }, [columnSizing, storageKey]);

  return [columnSizing, setColumnSizing] as const;
}

export function defaultPersistentColumnSizing<TColumnId extends string>(
  columns: readonly PersistedColumnDefinition<TColumnId>[],
): Record<TColumnId, number> {
  return Object.fromEntries(
    columns.map((column) => [column.id, column.defaultWidth]),
  ) as Record<TColumnId, number>;
}

function readStoredPersistentColumnSizing<TColumnId extends string>(
  storageKey: string,
  columns: readonly PersistedColumnDefinition<TColumnId>[],
): Record<TColumnId, number> {
  const fallback = defaultPersistentColumnSizing(columns);
  if (typeof window === "undefined") return fallback;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return fallback;
    const next = { ...fallback };
    for (const column of columns) {
      const width = (parsed as Record<string, unknown>)[column.id];
      if (typeof width === "number" && Number.isFinite(width)) {
        next[column.id] = clampColumnWidth(column, width);
      }
    }
    return next;
  } catch {
    return fallback;
  }
}

export function usePersistedFixedColumnSizing<TColumnId extends string>(
  storageKey: string,
  columns: readonly PersistedColumnDefinition<TColumnId>[],
) {
  const [columnSizing, setColumnSizing] = useState<Record<TColumnId, number>>(
    () => defaultPersistentColumnSizing(columns),
  );
  const [hasLoadedStoredSizing, setHasLoadedStoredSizing] = useState(false);

  useEffect(() => {
    setColumnSizing(readStoredPersistentColumnSizing(storageKey, columns));
    setHasLoadedStoredSizing(true);
  }, [columns, storageKey]);

  useEffect(() => {
    if (!hasLoadedStoredSizing || typeof window === "undefined") return;
    window.localStorage.setItem(storageKey, JSON.stringify(columnSizing));
  }, [columnSizing, hasLoadedStoredSizing, storageKey]);

  return [columnSizing, setColumnSizing] as const;
}
